import { Injectable } from '@angular/core';
import { User } from '../models/user';
import { Observable, catchError, map, of, shareReplay, switchMap, throwError, timeout } from 'rxjs';
import { Project, ProjectRole } from '../models/project';
import { EntraGroupMapping } from '../models/entraGroupMapping';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { protectedResources } from '../auth-config';

interface WizUser {
  Email: string;
}

const SAML_USER_REQUEST_TIMEOUT_MS = 10000;

@Injectable({
  providedIn: 'root',
})
export class UserService {
  url = protectedResources.apiUsers.endpoint;

  constructor(private httpClient: HttpClient) {}

  private buildAddRoleAssignmentUrl(
    projectId: string,
    email: string,
    role: ProjectRole
  ): string {
    return `${this.url}/AddRoleAssignment?projectId=${encodeURIComponent(
      projectId
    )}&email=${encodeURIComponent(email)}&role=${encodeURIComponent(role)}`;
  }

  private buildRemoveProjectAssignmentUrl(
    projectId: string,
    email: string,
    projectRole?: ProjectRole
  ): string {
    let assignmentUrl = `${this.url}/RemoveProjectAssignment?projectId=${encodeURIComponent(
      projectId
    )}&email=${encodeURIComponent(email)}`;

    if (projectRole) {
      assignmentUrl += `&projectRole=${encodeURIComponent(projectRole)}`;
    }

    return assignmentUrl;
  }

  private unwrapUserResponse(user: any): any {
    if (!user || typeof user !== 'object') {
      return user;
    }

    if ('body' in user) {
      const wrappedBody = user.body;

      if (typeof wrappedBody === 'string') {
        try {
          return JSON.parse(wrappedBody);
        } catch {
          return user;
        }
      }

      if (wrappedBody && typeof wrappedBody === 'object') {
        return wrappedBody;
      }
    }

    if ('data' in user && user.data && typeof user.data === 'object') {
      return user.data;
    }

    return user;
  }

  private normalizeUser(user: any): User {
    return {
      ...(user ?? {}),
      id: user?.id ?? user?.Id ?? '',
      name: user?.name ?? user?.Name ?? '',
      email: user?.email ?? user?.Email ?? '',
    } as User;
  }

  private isNotFoundError(error: any): boolean {
    const status = error?.status;
    if (status === 404) {
      return true;
    }

    const errorText = [
      error?.error?.message,
      error?.error?.title,
      error?.error?.detail,
      error?.message,
      error?.statusText,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return errorText.includes('not found');
  }

  public getUser(email: string): Observable<User> {
    return this.httpClient
      .get<User>(`${this.url}/SamlUser?email=${email}`)
      .pipe(
        map((user) => this.normalizeUser(this.unwrapUserResponse(user))),
        timeout(SAML_USER_REQUEST_TIMEOUT_MS),
      );
  }

  public createSamlUser(email: string): Observable<User> {
    const user: WizUser = { Email: email };

    return this.httpClient
      .post<User>(`${this.url}/`, user)
      .pipe(
        map((createdUser) => this.normalizeUser(createdUser)),
        timeout(SAML_USER_REQUEST_TIMEOUT_MS),
        shareReplay(1)
      );
  }

  public ensureSamlUser(email: string): Observable<User> {
    return this.getUser(email)
      .pipe(
        switchMap((user) => {
          // Some backends return 200 with an empty user payload instead of 404.
          if (user?.email) {
            return of(user);
          }
          return this.createSamlUser(email);
        }),
        catchError((error: HttpErrorResponse) => {
          if (this.isNotFoundError(error)) {
            return this.createSamlUser(email);
          }
          return throwError(() => error);
        }),
        shareReplay(1)
      );
  }

  public getUsers(): Observable<User[]> {
    return this.httpClient.get<User[]>(`${this.url}`).pipe(shareReplay(1));
  }

  public getProjectUsers(id: string, forceRefresh: boolean = false): Observable<Project> {
    const requestOptions = forceRefresh
      ? {
          params: { _ts: Date.now().toString() },
        }
      : undefined;

    return this.httpClient
      .get<Project>(`${this.url}/${id}`, requestOptions)
      .pipe(timeout(20000), shareReplay(1));
  }

  public postUser(user: User): Observable<User> {
    return this.httpClient.post<User>(`${this.url}`, user).pipe(shareReplay(1));
  }

  public addUserToProject(projectId: string, email: string): Observable<User> {
    return this.addUserToProjectByRole(projectId, email, 'Reader');
  }

  public addUserToProjectByRole(
    projectId: string,
    email: string,
    projectRole: ProjectRole
  ): Observable<User> {
    return this.httpClient
      .post<User>(this.buildAddRoleAssignmentUrl(projectId, email, projectRole), '')
      .pipe(shareReplay(1));
  }

  public removeUserFromProject(
    projectId: string,
    email: string
  ): Observable<User> {
    return this.httpClient
      .post<User>(this.buildRemoveProjectAssignmentUrl(projectId, email), '')
      .pipe(shareReplay(1));
  }

  public removeUserFromProjectByRole(
    projectId: string,
    email: string,
    projectRole: ProjectRole
  ): Observable<User> {
    return this.httpClient
      .post<User>(
        this.buildRemoveProjectAssignmentUrl(projectId, email, projectRole),
        ''
      )
      .pipe(shareReplay(1));
  }

  public deleteUser(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.url}`).pipe(shareReplay(1));
  }

  public editUser(user: User): Observable<User> {
    return this.httpClient.put<User>(`${this.url}`, user).pipe(shareReplay(1));
  }

  public createLoginAuditLog(eventType: string): Observable<any> {
    return this.httpClient
      .post<any>(`${this.url}/CreateLogInAuditLog?eventType=${eventType}`, '')
      .pipe(shareReplay(1));
  }

  // Entra Group Mapping methods
  public addEntraGroupToProject(
    projectId: string,
    groupObjectId: string
  ): Observable<EntraGroupMapping> {
    return this.httpClient
      .post<EntraGroupMapping>(
        `${this.url}/AddEntraGroupRoleAssignments?projectId=${encodeURIComponent(projectId)}&groupObjectId=${encodeURIComponent(groupObjectId)}`,
        ''
      )
      .pipe(shareReplay(1));
  }

  public removeEntraGroupMapping(
    projectId: string,
    groupObjectId: string
  ): Observable<void> {
    return this.httpClient
      .post<void>(
        `${this.url}/RemoveEntraGroupRoleAssignments?projectId=${encodeURIComponent(projectId)}&groupObjectId=${encodeURIComponent(groupObjectId)}`,
        ''
      )
      .pipe(shareReplay(1));
  }
}

